import { useState, useCallback, useEffect } from 'react'
import { AddressInputs, EmailInput, PhoneInput, TextInput } from '../../components'
import { Trustee } from '@oneethos/shared'
import { ConsumerAppFormData, ConsumerAppUpdateFn } from '.'

type TrusteeFormProps = {
  update: ConsumerAppUpdateFn,
  formData: ConsumerAppFormData,
  onClose?: () => void,
  initialData?: Trustee | null,
  isEditing?: boolean
  onFormCompletionChange?: (isComplete: boolean) => void
}

const TrusteeForm = ({
  update,
  formData,
  onClose,
  initialData = null,
  isEditing = false,
  onFormCompletionChange}: TrusteeFormProps) => {

  const [trusteeForm, setTrusteeForm] = useState<Trustee>({
    name: '',
    email: '',
    phone: '',
    address: {
      streetAddress: '',
      streetAddressLine2: '',
      city: '',
      state: '',
      zip: '',
      country: '',
    }
  });

  useEffect(() => {
    if (initialData) {
      setTrusteeForm(initialData)
    }
  }, [initialData])

  const isComplete = !!(trusteeForm.name &&
    trusteeForm.email &&
    trusteeForm.phone &&
    trusteeForm.address?.streetAddress &&
    trusteeForm.address?.city &&
    trusteeForm.address?.state &&
    trusteeForm.address?.zip)
  
  useEffect(() => {
    if (onFormCompletionChange) {
      onFormCompletionChange(isComplete);
    }
  }, [isComplete, onFormCompletionChange])

  const resetForm = () => {
    setTrusteeForm({
      name: '',
      email: '',
      phone: '',
      address: {
        streetAddress: '',
        streetAddressLine2: '',
        city: '',
        state: '',
        zip: '',
        country: '',
      }
    })
  }

  const updateTrusteeInFormData = useCallback((updatedTrustee) => {
    if (isEditing && initialData) {
      const updatedTrustees = formData.trustees.map(trustee =>
        trustee === initialData ? updatedTrustee : trustee
      )
      update('trustees', updatedTrustees)
    }
  }, [isEditing, initialData, formData.trustees, update])

  const handleFieldChange = useCallback((field, value) => {
    const updatedForm = { ...trusteeForm }
    updatedForm[field] = value
    setTrusteeForm(updatedForm)

    if (isEditing && initialData) {
      updateTrusteeInFormData(updatedForm)
    }
  }, [trusteeForm, isEditing, initialData, updateTrusteeInFormData])

  const handleSubmit = useCallback(() => {
    if (isComplete && !isEditing) {
      update('trustees', [...formData.trustees, trusteeForm])
      resetForm()

      if (onClose) {
        onClose()
      }
    }
  }, [trusteeForm, update, formData.trustees, isComplete, onClose, isEditing])

  return (
    <div className="form-group border rounded p-3 mb-3">
      <h4>{!isEditing && 'Add New Trustee'}</h4>
      <div className="form-group">
        <label>Legal Name</label>
        <TextInput
          value={trusteeForm?.name}
          onChange={val => handleFieldChange('name', val)}
        />
      </div>
      <div className="form-group">
        <label>Email</label>
        <EmailInput
          value={trusteeForm?.email}
          onChange={val => handleFieldChange('email', val)}
        />
      </div>
      <div className="form-group">
        <label>Phone</label>
        <PhoneInput
          value={trusteeForm?.phone}
          onChange={val => handleFieldChange('phone', val)}
        />
      </div>
      <AddressInputs
        label='Address'
        address={trusteeForm?.address}
        onChange={val => handleFieldChange('address', val)}
      />
      <div className="form-group text-end">
        {!isEditing && (
          <button
            className='btn btn-secondary'
            type='button'
            onClick={handleSubmit}
            disabled={!isComplete}
          >
            Save
          </button>
        )}
        <button
          className={`btn btn-outline-secondary ${!isEditing ? 'ms-2' : ''}`}
          type='button'
          onClick={onClose}
        >
          {isEditing ? 'Remove' : 'Cancel'}
        </button>
      </div>
    </div>
  )
}

export default TrusteeForm
